import { useQuery } from "@tanstack/react-query";
import type { UseQueryResult } from "@tanstack/react-query";
import { useSession } from "../app/session.js";
import { fetchAuthConfig, fetchMe } from "../api/auth.js";
import type { AuthConfig, Me } from "../api/auth.js";
import { DEFAULT_POLICIES, fetchAdminPolicies } from "../api/policies.js";
import type { AdminPolicies, Policies } from "../api/policies.js";
import { MIN_MASTER_LENGTH } from "../lib/masterPassword.js";
import { fetchMailStatus, listSystemAudit, listUsers } from "../api/admin.js";
import type { AdminUser, MailStatus, SystemAuditEvent } from "../api/admin.js";
import { fetchAccountKeys } from "../api/account.js";
import type { AccountKeyState } from "../api/account.js";
import { listMySessions, listUserSessions } from "../api/sessions.js";
import type { DeviceSession } from "../api/sessions.js";
import { listMyRememberedDevices, listUserRememberedDevices } from "../api/deviceKeys.js";
import type { RememberedDevice } from "../api/deviceKeys.js";
import { fetchRecoveryOverview } from "../api/recovery.js";
import type { RecoveryOverview } from "../api/recovery.js";

export const ME_KEY: ReadonlyArray<string> = ["account", "me"];
export const ADMIN_USERS_KEY: ReadonlyArray<string> = ["admin", "users"];
export const ADMIN_AUDIT_KEY: ReadonlyArray<string> = ["admin", "audit"];
export const ADMIN_MAIL_KEY: ReadonlyArray<string> = ["admin", "mail"];
export const SESSIONS_KEY: ReadonlyArray<string> = ["account", "sessions"];
export const REMEMBERED_DEVICES_KEY: ReadonlyArray<string> = ["account", "remembered-devices"];
export const ACCOUNT_KEYS_KEY: ReadonlyArray<string> = ["account", "keys"];
export const ADMIN_POLICIES_KEY: ReadonlyArray<string> = ["admin", "policies"];
export const AUTH_CONFIG_KEY: ReadonlyArray<string> = ["auth", "config"];
export const ADMIN_RECOVERY_KEY: ReadonlyArray<string> = ["admin", "recovery"];

export function userSessionsKey(userId: string): ReadonlyArray<string> {
  return ["admin", "users", userId, "sessions"];
}

export function useMe(): UseQueryResult<Me, Error> {
  const unlocked = useSession().state.status === "unlocked";
  return useQuery<Me, Error>({ queryKey: ME_KEY, queryFn: fetchMe, enabled: unlocked, staleTime: 60_000 });
}

/** The instance policies as /me reported them; the defaults until it has loaded. */
export function usePolicies(): Policies {
  return useMe().data?.policies ?? DEFAULT_POLICIES;
}

/**
 * Policy minimum for new master passwords. Read from the public config, so it also works before
 * sign-in (recovery, first password).
 */
export function useMinPasswordLength(): number {
  const config = useQuery<AuthConfig, Error>({ queryKey: AUTH_CONFIG_KEY, queryFn: fetchAuthConfig, staleTime: 60_000, retry: false });
  return config.data?.minPasswordLength ?? MIN_MASTER_LENGTH;
}

export function useAdminPolicies(enabled: boolean): UseQueryResult<AdminPolicies, Error> {
  return useQuery<AdminPolicies, Error>({ queryKey: ADMIN_POLICIES_KEY, queryFn: fetchAdminPolicies, enabled });
}

/** Only fetches when the account is an admin with 2FA; the server refuses everyone else anyway. */
export function useRecoveryOverview(enabled: boolean): UseQueryResult<RecoveryOverview | null, Error> {
  return useQuery<RecoveryOverview | null, Error>({ queryKey: ADMIN_RECOVERY_KEY, queryFn: fetchRecoveryOverview, enabled, refetchOnMount: "always" });
}

export function useAdminUsers(enabled: boolean): UseQueryResult<AdminUser[], Error> {
  // Other accounts change on their own (sign-ins, enrollment), so opening the page always reloads.
  return useQuery<AdminUser[], Error>({ queryKey: ADMIN_USERS_KEY, queryFn: listUsers, enabled, refetchOnMount: "always" });
}

export function useMailStatus(enabled: boolean): UseQueryResult<MailStatus, Error> {
  return useQuery<MailStatus, Error>({ queryKey: ADMIN_MAIL_KEY, queryFn: fetchMailStatus, enabled, staleTime: 60_000 });
}

export function useSystemAudit(enabled: boolean): UseQueryResult<SystemAuditEvent[], Error> {
  return useQuery<SystemAuditEvent[], Error>({ queryKey: ADMIN_AUDIT_KEY, queryFn: listSystemAudit, enabled });
}

/** Sealed account keys and the emergency kit date. */
export function useAccountKeys(): UseQueryResult<AccountKeyState, Error> {
  const unlocked = useSession().state.status === "unlocked";
  return useQuery<AccountKeyState, Error>({ queryKey: ACCOUNT_KEYS_KEY, queryFn: fetchAccountKeys, enabled: unlocked, staleTime: 60_000 });
}

export function useMySessions(): UseQueryResult<DeviceSession[], Error> {
  const unlocked = useSession().state.status === "unlocked";
  return useQuery<DeviceSession[], Error>({ queryKey: SESSIONS_KEY, queryFn: listMySessions, enabled: unlocked, refetchOnMount: "always" });
}

export function userRememberedDevicesKey(userId: string): ReadonlyArray<string> {
  return ["admin", "users", userId, "remembered-devices"];
}

export function useMyRememberedDevices(): UseQueryResult<RememberedDevice[], Error> {
  const unlocked = useSession().state.status === "unlocked";
  return useQuery<RememberedDevice[], Error>({ queryKey: REMEMBERED_DEVICES_KEY, queryFn: listMyRememberedDevices, enabled: unlocked, refetchOnMount: "always" });
}

export function useUserRememberedDevices(userId: string, enabled: boolean): UseQueryResult<RememberedDevice[], Error> {
  return useQuery<RememberedDevice[], Error>({
    queryKey: userRememberedDevicesKey(userId),
    queryFn: (): Promise<RememberedDevice[]> => listUserRememberedDevices(userId),
    enabled
  });
}

export function useUserSessions(userId: string, enabled: boolean): UseQueryResult<DeviceSession[], Error> {
  return useQuery<DeviceSession[], Error>({
    queryKey: userSessionsKey(userId),
    queryFn: (): Promise<DeviceSession[]> => listUserSessions(userId),
    enabled
  });
}
